// WDCE-199 step 9g-3a (D56): what the EFTPOS panel shows, worked out from paymentScreen.js's state, so the panel's JSX
// (9g-3b) only renders it. No React, fetch or DOM here, so it can be tested with bin/js-test.
//
// - paneEnabled: whether the panel, its session and its page-load lookup exist at all. Only on an open (editable) POS
//   order whose payment types include EFTPOS (listed only when it's offered, step 9h-3), for someone who may take it
//   (canPayByEftpos).
// - panelView: the heading, text, amount, PIN pad display, keys, Pay button and admin link for a state, and whether
//   the panel is active (it then replaces the card form and the main button whatever payment radio is ticked).
// - showsPanel (step 9g-3b): whether OrderForm shows the panel in place of the card form and its main button: when it
//   has one, and EFTPOS is the ticked radio or the panel is active (after a reload no radio is ticked).
// - reachedApproval: whether this page saw a payment it was following become approved, so it should reload onto the
//   paid page as a cash checkout does. Never from ready: the page-load lookup can find an approved payment on an order
//   still open (marking it paid failed, or more was approved than its total), and a reload would only find it again.

import { formatCents } from './money'
import { canPay, keyButtons, payBlockedReason } from './paymentScreen'

const NO_PIN_PAD = 'Choose a PIN pad first, with the PIN pad control at the top of the page.'
const RESOLVE = 'An Administrator must resolve this payment before another can be taken.'
const PAY_AGAIN = 'Pay again by EFTPOS'
const PAY_THE_REST = 'Pay the rest by EFTPOS'

const HEADINGS = {
  ready: 'Pay by EFTPOS',
  starting: 'Starting the payment',
  waiting: 'Waiting for the PIN pad',
  inProgress: 'Payment in progress',
  approved: 'Payment approved',
  partlyPaid: 'Part paid',
  declined: 'Payment declined',
  cancelled: 'Payment cancelled',
  failedToStart: "Payment didn't start",
  checking: 'Checking the payment',
  unknown: 'Payment result unknown',
  notFound: 'Payment not found',
  refused: 'Payment not started'
}
// Where the Pay button shows: everywhere a payment can be started, and (disabled) while one is starting or checked.
const PAY_SHOWN = ['ready', 'starting', 'checking', 'partlyPaid', 'declined', 'cancelled', 'failedToStart', 'notFound',
  'refused']
const FAILED = ['declined', 'cancelled', 'failedToStart']
// A payment this page may be following: one of these turning approved means the order was just paid.
const FOLLOWED = ['waiting', 'inProgress', 'unknown', 'checking']
const ACTIVE_PHASES = ['starting', 'waiting', 'inProgress', 'unknown', 'checking', 'approved']
const IN_FLIGHT = ['pending', 'in_progress', 'unknown']

const isObject = (value) => value !== null && typeof value === 'object' && !Array.isArray(value)

const money = (cents) => (Number.isSafeInteger(cents) ? formatCents(cents) : null)

export function paneEnabled({ editable, canPayByEftpos, paymentTypes } = {}) {
  return editable === true && canPayByEftpos === true && isObject(paymentTypes) &&
    Object.prototype.hasOwnProperty.call(paymentTypes, 'eftpos')
}

export function showsPanel({ hasPanel, paymentType, active } = {}) {
  return hasPanel === true && (paymentType === 'eftpos' || active === true)
}

export function reachedApproval(previous, next) {
  return isObject(previous) && isObject(next) && FOLLOWED.includes(previous.phase) && next.phase === 'approved'
}

// Why the amount left to pay isn't known (the server's reason), or null.
function amountUnknownReason(state) {
  if (state.phase === 'ready') return state.amountText === null ? state.message : null

  return state.payment && state.payment.remainingUnknownReason ? state.payment.remainingUnknownReason : null
}

function remainingCents(state) {
  return state.payment ? money(state.payment.remainingCents) : null
}

function lines(state, { approvedHere }) {
  switch (state.phase) {
    case 'ready': return state.message ? [state.message] : []
    case 'inProgress': return state.displayLines.length > 0 ? [] : [state.message || 'Follow the PIN pad.']
    case 'approved':
      return approvedHere
        ? ['Payment approved. Opening the paid order...']
        : ['Ask an Administrator to check this payment before taking any other payment.']
    case 'partlyPaid': {
      const rest = remainingCents(state)
      return [rest ? `Payment approved. ${rest} still to pay.` : 'Payment approved.']
    }
    case 'unknown': return [state.message, RESOLVE]
    case 'notFound': return ['Check the PIN pad. If it shows no payment, pay again.']
    default: {
      const rest = state.eftposApproved && FAILED.includes(state.phase) ? remainingCents(state) : null
      return [state.message].concat(rest ? [`${rest} still to pay.`] : [])
    }
  }
}

function heading(state, { approvedHere }) {
  if (state.phase === 'approved' && !approvedHere) return "Payment approved, but the order isn't marked paid"

  return HEADINGS[state.phase]
}

function amountLine(state, pinPadName) {
  if (!state.amountText) return null
  if (state.phase === 'ready') {
    return pinPadName ? `${state.amountText} to pay on ${pinPadName}.` : `${state.amountText} to pay.`
  }
  if (['waiting', 'inProgress'].includes(state.phase)) {
    return pinPadName ? `${state.amountText} on ${pinPadName}.` : `${state.amountText}.`
  }
  return null
}

function payLabel(state, checkingOut) {
  if (checkingOut) return 'Saving...'
  if (state.phase === 'partlyPaid') {
    const rest = remainingCents(state)
    return rest ? `Pay the rest (${rest}) by EFTPOS` : PAY_THE_REST
  }
  if (FAILED.includes(state.phase)) return state.eftposApproved ? PAY_THE_REST : PAY_AGAIN
  if (['notFound', 'refused'].includes(state.phase)) return PAY_AGAIN

  return state.amountText ? `Pay ${state.amountText} by EFTPOS` : 'Pay by EFTPOS'
}

function payButton(state, { checkingOut, eftposTerminal, canPayByEftpos }) {
  const pinPad = { eftposTerminal, canPayByEftpos }
  const reason = amountUnknownReason(state)
  const blockedReason = !PAY_SHOWN.includes(state.phase) ? null
    : payBlockedReason(state, pinPad) ? NO_PIN_PAD : reason

  return {
    shown: PAY_SHOWN.includes(state.phase),
    enabled: canPay(state, pinPad) && !checkingOut && !reason,
    label: payLabel(state, checkingOut),
    blockedReason,
    // Once part is paid, the checkout PATCH is refused (9g-2), so Pay goes straight to the start.
    checkoutFirst: !state.eftposApproved
  }
}

export function panelView({
  state, keyBusy = false, checkingOut = false, approvedHere = false, eftposTerminal = null, canPayByEftpos = false,
  eftposAdminPaymentsPath = null
}) {
  const payment = state.payment
  const pinPadName = (payment && payment.pinPadName) || (eftposTerminal && eftposTerminal.name) || null
  const saving = checkingOut && PAY_SHOWN.includes(state.phase)
  const keysDisabled = keyBusy || state.keySending !== null

  return {
    active: state.eftposApproved || ACTIVE_PHASES.includes(state.phase) ||
      Boolean(payment && IN_FLIGHT.includes(payment.status)),
    heading: saving ? HEADINGS.ready : heading(state, { approvedHere }),
    lines: saving ? ['Saving the order...'] : lines(state, { approvedHere }),
    amountLine: amountLine(state, pinPadName),
    displayLines: state.phase === 'inProgress' ? state.displayLines : [],
    keys: keyButtons(state.offeredKeys).map((button) => ({ ...button, disabled: keysDisabled })),
    keyMessage: state.keyMessage,
    pay: payButton(state, { checkingOut: saving, eftposTerminal, canPayByEftpos }),
    adminLink: eftposAdminPaymentsPath && payment && payment.id != null ? `${eftposAdminPaymentsPath}/${payment.id}`
      : null,
    reload: approvedHere && state.phase === 'approved'
  }
}
